import React, { useState, useEffect } from 'react';
import { Shield, MapPin } from 'lucide-react';

const TRACKING_KEY = 'pa_tracking_consent';
const LOCATION_KEY = 'pa_user_location';
const HABITS_KEY = 'pa_user_habits';

const getConsent = () => localStorage.getItem(TRACKING_KEY);

// Analytics event helpers (GA4 + FB Pixel)
export const trackEvent = (eventName, params = {}) => {
  try {
    if (window.gtag) window.gtag('event', eventName, params);
    if (window.fbq) window.fbq('track', eventName, params);
  } catch {}
};

export const trackLead = (source, data = {}) => {
  trackEvent('Lead', { content_name: source, ...data });
  trackEvent('generate_lead', { event_category: source, ...data });
};

export const trackViewContent = (name, category) => {
  trackEvent('ViewContent', { content_name: name, content_category: category });
  trackEvent('view_item', { item_name: name, item_category: category });
};

// Track page visits, time on page, properties viewed
const trackHabit = (type, data) => {
  try {
    const habits = JSON.parse(localStorage.getItem(HABITS_KEY) || '{"pages":[],"properties":[],"sessions":0}');
    if (type === 'page') {
      habits.pages.push({ path: data, ts: new Date().toISOString() });
      if (habits.pages.length > 200) habits.pages = habits.pages.slice(-200);
    } else if (type === 'property') {
      habits.properties.push({ id: data, ts: new Date().toISOString() });
      if (habits.properties.length > 100) habits.properties = habits.properties.slice(-100);
    } else if (type === 'session') {
      habits.sessions = (habits.sessions || 0) + 1;
      habits.lastVisit = new Date().toISOString();
    }
    localStorage.setItem(HABITS_KEY, JSON.stringify(habits));
  } catch {}
};

// Request user's geolocation
const requestLocation = () => {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: new Date().toISOString() };
      localStorage.setItem(LOCATION_KEY, JSON.stringify(loc));
    },
    () => {}
  );
};

export const initTracking = (pathname) => {
  if (getConsent() !== 'accepted') return;
  trackHabit('page', pathname);
};

export const trackPropertyView = (propertyId) => {
  if (getConsent() !== 'accepted') return;
  trackHabit('property', propertyId);
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
    if (consent === 'accepted') {
      trackHabit('session', null);
      requestLocation();
    }
  }, []);

  const accept = () => {
    localStorage.setItem(TRACKING_KEY, 'accepted');
    setVisible(false);
    trackHabit('session', null);
    requestLocation();
  };

  const decline = () => {
    localStorage.setItem(TRACKING_KEY, 'declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent" className="fixed bottom-0 left-0 right-0 z-[55] p-4 pb-6 animate-fade-in pointer-events-none">
      <div className="container-custom pointer-events-auto">
        <div className="bg-obsidian-card border border-white/10 rounded-xl p-5 md:p-6 backdrop-blur-xl shadow-2xl max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-brand/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield size={20} className="text-brand" />
              </div>
              <div>
                <h3 className="font-outfit text-white text-sm font-semibold mb-1">We value your privacy</h3>
                <p className="text-white/50 text-xs font-dm-sans leading-relaxed">
                  We use cookies and location data to personalize your experience, show relevant properties, and improve our services. 
                  By accepting, you allow us to track browsing habits for better recommendations.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 flex-shrink-0 w-full md:w-auto">
              <button onClick={decline} data-testid="cookie-decline"
                className="flex-1 md:flex-initial px-5 py-2.5 rounded-lg border border-white/15 text-white/60 hover:text-white hover:border-white/30 text-xs font-outfit font-medium transition-all">
                Decline
              </button>
              <button onClick={accept} data-testid="cookie-accept"
                className="flex-1 md:flex-initial px-5 py-2.5 rounded-lg bg-brand-gradient text-white text-xs font-outfit font-semibold hover:shadow-lg hover:shadow-brand/20 transition-all flex items-center justify-center gap-1.5">
                <MapPin size={13} /> Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
